import { creatureIcon } from "../art/creatures.ts";
import { UI, withA, type Col } from "../art/pal.ts";
import type { SpeciesId } from "../data/species.ts";
import { TILE } from "../data/map.ts";
import { measureText, wrapPx } from "../engine/font.ts";
import { SCREEN_H, SCREEN_W, type Gfx } from "../engine/gfx.ts";
import type { Ctx } from "../game/ctx.ts";
import type { Peer } from "../net/client.ts";
import { EMOTES, CHAT_MAX, REACH_TILES } from "../net/protocol.ts";
import { glass, hint } from "../ui/kit.ts";
import { ACTOR_AX, ACTOR_AY, actorFrame, peerOutfit, type Facing, type Outfit } from "../world/actors.ts";

export interface PeerDrawable {
  y: number;
  x: number;
  top: number;
  img: ImageData;
  shadow: boolean;
  tie: number;
}

const outfits = new Map<string, Outfit>();
function outfitKey(p: Peer): string {
  return `peer:${p.look.hue}:${p.look.sticker ? 1 : 0}:${p.look.outfit ?? ""}`;
}
function outfitOf(p: Peer): Outfit {
  const key = outfitKey(p);
  let o = outfits.get(key);
  if (!o) outfits.set(key, (o = peerOutfit(p.look.hue, p.look.sticker, p.look.outfit)));
  return o;
}

/** World-pixel feet position of a remote scout, eased between tiles. */
function peerFeet(p: Peer): { x: number; y: number; t: number } {
  const t = p.moving ? Math.min(1, p.stepT / p.stepMs) : 1;
  return { x: (p.fromX + (p.x - p.fromX) * t) * TILE + 8, y: (p.fromY + (p.y - p.fromY) * t) * TILE + 15, t };
}

/** Where a Crown follower walks: just behind its scout (dir 0 up, 1 down, 2 right, 3 left). */
export function followerSpot(feetX: number, feetY: number, dir: number): { x: number; y: number } {
  // Facing the camera, straight behind would hide it: it walks at the scout's shoulder instead.
  const off = [[0, 9], [-10, -4], [-11, 1], [11, 1]][dir] ?? [-10, -4];
  return { x: feetX + off[0]!, y: feetY + off[1]! };
}

/** A scout's lead showcase Crown, hopping along behind them. */
export function followerDrawable(species: string, shiny: boolean, feetX: number, feetY: number, dir: number, anim: number, reduced: boolean): PeerDrawable {
  const f = followerSpot(feetX, feetY, dir);
  const hop = reduced ? 0 : Math.floor(anim / 260) % 2;
  return { y: f.y, x: f.x - 8, top: f.y - 15 - hop, img: creatureIcon(species as SpeciesId, shiny), shadow: true, tie: 1 };
}

let plinth: ImageData | null = null;
/** Gilded Plinth: a gold ring the follower stands on (drawn just before it). */
export function plinthDrawable(feetX: number, feetY: number, dir: number): PeerDrawable {
  if (!plinth) {
    const w = 18, h = 7;
    plinth = new ImageData(w, h);
    for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
      const dx = (x + 0.5 - w / 2) / (w / 2), dy = (y + 0.5 - h / 2) / (h / 2);
      const r = dx * dx + dy * dy;
      if (r > 1) continue;
      const c: Col = r > 0.55 ? [196, 140, 52, 255] : y < h / 2 ? [255, 226, 150, 255] : [232, 180, 80, 255];
      plinth.data.set(c, (y * w + x) * 4);
    }
  }
  const f = followerSpot(feetX, feetY, dir);
  return { y: f.y - 0.5, x: f.x - 9, top: f.y - 4, img: plinth, shadow: false, tie: 1 };
}

/** Starlight Trail: a few twinkling motes around the follower. */
export function trailMotes(g: Gfx, sx: number, sy: number, anim: number, reduced: boolean): void {
  if (reduced) return;
  for (let k = 0; k < 4; k++) {
    const t = (anim / 700 + k / 4) % 1;
    const x = Math.round(sx + Math.sin(k * 2.1 + anim / 500) * 9);
    const y = Math.round(sy - 4 - t * 14);
    g.p(x, y, withA(UI.goldHi, Math.round(220 * (1 - t))));
    if (k % 2 === 0) g.p(x + 1, y, withA(UI.gold, Math.round(160 * (1 - t))));
  }
}

type Menu = { peer: Peer; sel: number } | null;
const MENU = ["DUEL", "TRADE", "WAVE", "CLOSE"] as const;

/**
 * Everything the live world adds to the street: other scouts, name tags, chat and emote bubbles,
 * the A-menu on another player, incoming duel/trade prompts and a short toast line.
 */
export function onlineField(ctx: Ctx) {
  let menu: Menu = null;
  let toast: { text: string; at: number } | null = null;
  let chatEl: HTMLInputElement | null = null;
  let chatOpen = false;
  let selfChat: { text: string; at: number } | null = null;
  let selfEmote: { e: number; at: number } | null = null;

  ctx.net.on((msg) => {
    if (msg.t === "chat" && msg.id === ctx.net.id) selfChat = { text: msg.text, at: ctx.now };
    if (msg.t === "emote" && msg.id === ctx.net.id) selfEmote = { e: msg.e, at: ctx.now };
    if (msg.t === "reqResult" && msg.accepted) toast = { text: msg.kind === "duel" ? "Duel accepted!" : "Trade accepted!", at: ctx.now };
  });

  function say(text: string): void {
    toast = { text, at: ctx.now };
  }

  function chatInput(): HTMLInputElement | null {
    if (chatEl) return chatEl;
    if (typeof document === "undefined" || typeof document.createElement !== "function") return null;
    const host = document.getElementById("bezel");
    if (!host) return null;
    const el = document.createElement("input");
    el.id = "chat";
    el.maxLength = CHAT_MAX;
    el.placeholder = "Say something… (Enter to send, Esc to close)";
    el.autocomplete = "off";
    el.spellcheck = false;
    el.addEventListener("keydown", (e) => {
      e.stopPropagation();
      if (e.key === "Enter") {
        const text = el.value.trim();
        if (text) ctx.net.send({ t: "chat", text });
        closeChat();
      } else if (e.key === "Escape") closeChat();
    });
    el.addEventListener("blur", () => closeChat());
    host.appendChild(el);
    chatEl = el;
    return el;
  }

  function openChat(): void {
    const el = chatInput();
    if (!el || !ctx.net.live) return;
    el.value = "";
    el.style.fontSize = `${Math.max(12, Math.round((el.parentElement?.clientHeight ?? 400) * 0.045))}px`;
    el.classList.add("open");
    chatOpen = true;
    setTimeout(() => el.focus(), 0);
  }

  function closeChat(): void {
    if (!chatEl) return;
    chatEl.classList.remove("open");
    chatOpen = false;
    chatEl.blur();
    ctx.input.reset();
  }

  function peerAt(x: number, y: number): Peer | null {
    for (const p of ctx.net.peers.values()) if (p.x === x && p.y === y) return p;
    return null;
  }

  return {
    get chatOpen() {
      return chatOpen;
    },
    peerAt,
    openMenu(peer: Peer) {
      menu = { peer, sel: 0 };
      ctx.audio.confirm();
    },
    /** Returns true while a live-world modal owns the input this frame. */
    update(idle: boolean): boolean {
      const net = ctx.net;
      // One toast at a time: badges, missions and server notices queue up instead of overwriting.
      if (!toast || ctx.now - toast.at > 2200) {
        const n = net.takeNotice();
        if (n) say(n);
      }
      if (chatOpen) return true;
      if (!net.live) {
        menu = null;
        return false;
      }
      // Incoming duel / trade prompt.
      const req = net.requests[0];
      if (req && idle) {
        if (ctx.input.consume("Enter")) {
          net.send({ t: "reqAnswer", from: req.from, kind: req.kind, accept: true });
          net.requests.shift();
          ctx.audio.confirm();
        } else if (ctx.input.consume("Escape")) {
          net.send({ t: "reqAnswer", from: req.from, kind: req.kind, accept: false });
          net.requests.shift();
          ctx.audio.back();
        }
        return true;
      }
      if (menu) {
        if (!net.peers.has(menu.peer.id)) {
          menu = null;
          return false;
        }
        const d = ctx.input.menuDir(ctx.now);
        if (d?.y) {
          menu.sel = (menu.sel + d.y + MENU.length) % MENU.length;
          ctx.audio.cursor();
        }
        if (ctx.input.consume("Escape")) {
          menu = null;
          ctx.audio.back();
        } else if (ctx.input.consume("Enter")) {
          const pick = MENU[menu.sel]!;
          const peer = menu.peer;
          menu = null;
          ctx.audio.confirm();
          if (pick === "DUEL" || pick === "TRADE") {
            const dist = Math.max(Math.abs(peer.x - ctx.state.x), Math.abs(peer.y - ctx.state.y));
            if (dist > REACH_TILES) say("Walk closer first.");
            else {
              net.send({ t: "req", kind: pick === "DUEL" ? "duel" : "trade", to: peer.id });
              say(`Asking ${peer.name}...`);
            }
          } else if (pick === "WAVE") net.send({ t: "emote", e: 0 });
        }
        return true;
      }
      if (!idle) return false;
      if (ctx.input.consumeRaw("t") || ctx.input.consumeRaw("c")) {
        openChat();
        return true;
      }
      for (let i = 0; i < EMOTES.length; i++) {
        if (ctx.input.consumeRaw(String(i + 1))) {
          net.send({ t: "emote", e: i });
          return false;
        }
      }
      return false;
    },
    drawables(anim: number, reduced: boolean): PeerDrawable[] {
      const out: PeerDrawable[] = [];
      for (const p of ctx.net.peers.values()) {
        const f = peerFeet(p);
        const frame = p.moving && !reduced ? (f.t < 0.5 ? 1 : 3) : 0;
        const img = actorFrame(outfitKey(p), outfitOf(p), p.dir as Facing, frame);
        const bob = !p.moving && !reduced ? Math.floor((anim + p.look.hue * 11) / 600) % 2 : 0;
        out.push({ y: f.y, x: f.x - ACTOR_AX, top: f.y - ACTOR_AY - bob, img, shadow: true, tie: 1 });
        if (p.crown?.plinth) out.push(plinthDrawable(f.x, f.y, p.dir));
        if (p.crown) out.push(followerDrawable(p.crown.species, p.crown.shiny, f.x, f.y, p.dir, anim + p.look.hue * 37, reduced));
      }
      return out;
    },
    /** Name tags, bubbles, chat log, status pill and prompts, drawn after the day/night grade. */
    drawOverlay(g: Gfx, cx: number, cy: number, selfFeet: { x: number; y: number }, hudY: number, anim: number): void {
      const net = ctx.net;
      const now = ctx.now;
      for (const p of net.peers.values()) {
        const f = peerFeet(p);
        const sx = Math.round(f.x - cx), sy = Math.round(f.y - cy);
        if (sx < -60 || sx > SCREEN_W + 60 || sy < -10 || sy > SCREEN_H + 40) continue;
        if (p.crown?.trail) {
          const fs = followerSpot(f.x, f.y, p.dir);
          trailMotes(g, Math.round(fs.x - cx), Math.round(fs.y - cy), anim + p.look.hue * 37, ctx.reduced);
        }
        nameTag(g, sx, sy - 34, p.name, p.status === "duel" ? UI.pink : p.status === "trade" ? UI.teal : UI.text, p.status, p.title, { frame: p.frame, plaque: p.crown?.plaque, wallet: p.wallet });
        if (p.emote) emoteBubble(g, sx, sy - 44, EMOTES[p.emote.e] ?? "!", now - p.emote.at);
        else if (p.chat) chatBubble(g, sx, sy - 44, p.chat.text);
      }
      const px = Math.round(selfFeet.x - cx), py = Math.round(selfFeet.y - cy);
      if (selfEmote && now - selfEmote.at < 2400) emoteBubble(g, px, py - 36, EMOTES[selfEmote.e] ?? "!", now - selfEmote.at);
      else if (selfChat && now - selfChat.at < 6000) chatBubble(g, px, py - 36, selfChat.text);

      // Status pill under the money pill.
      if (net.enabled) {
        const label = net.status === "online" ? `${Math.max(net.online, net.peers.size + 1)} online`
          // Signed in elsewhere is the player's own doing; any other drop is the server (a deploy, most often).
          : net.full ? "Server full · retrying soon"
            : net.status === "connecting" ? "connecting" : net.status === "denied" ? "offline" : "Offline because DEV IS COOKING";
        const w = measureText(label) + 20;
        glass(g, 6, 26 + hudY, w, 14, { r: 7, alpha: 200 });
        const dot: Col = net.status === "online" ? UI.green : net.full || net.status === "connecting" ? UI.gold : [120, 120, 140, 255];
        g.disc(14, 33 + hudY, 2.5, dot);
        g.text(20, 30 + hudY, label, net.status === "online" ? UI.text : UI.textDim);
      }

      // Chat log (recent lines only).
      const lines = net.chatLog.filter((l) => now - l.at < 12_000).slice(-4);
      lines.forEach((l, i) => {
        const y = SCREEN_H - 34 - (lines.length - 1 - i) * 11;
        const a = Math.max(0, Math.min(1, (12_000 - (now - l.at)) / 1500));
        const text = `${l.name}: ${l.text}`;
        const rows = wrapPx(text, 200);
        g.fill(6, y - 2, Math.min(206, measureText(rows[0]!) + 8), 11, [10, 12, 26, Math.round(150 * a)]);
        g.textShadow(10, y, rows[0]!, withA(l.self ? UI.goldHi : UI.text, 255 * a), withA(UI.ink, 255 * a));
      });

      // Toast.
      if (toast && now - toast.at < 3200) {
        const w = measureText(toast.text) + 20;
        glass(g, Math.round((SCREEN_W - w) / 2), 48, w, 17, { r: 8, alpha: 230, tone: UI.bg3 });
        g.textShadow(Math.round((SCREEN_W - w) / 2) + 10, 53, toast.text, UI.text, UI.ink);
      }

      // Incoming request prompt.
      const req = net.requests[0];
      if (req) {
        const title = `${req.name} wants to ${req.kind === "duel" ? "DUEL" : "TRADE"}`;
        const w = Math.max(200, measureText(title) + 30);
        const x = Math.round((SCREEN_W - w) / 2), y = 70;
        const pulse = Math.abs(Math.sin(anim / 240));
        glass(g, x, y, w, 46, { r: 8, border: req.kind === "duel" ? UI.pink : UI.teal });
        g.rrect(x + 1, y + 1, w - 2, 3, 2, withA(req.kind === "duel" ? UI.pink : UI.teal, 150 + pulse * 100));
        g.textShadow(x + 12, y + 10, title, UI.text, UI.ink);
        const left = Math.max(0, Math.ceil((20_000 - (now - req.at)) / 1000));
        g.textR(x + w - 12, y + 10, `${left}s`, UI.textDim);
        hint(g, x + 12, y + 26, "A", "accept", "a");
        hint(g, x + 90, y + 26, "B", "decline", "b");
      }

      // Player menu.
      if (menu) {
        const p = menu.peer;
        const w = 132, h = 30 + MENU.length * 14;
        const x = Math.round(SCREEN_W / 2 + 24), y = Math.round(SCREEN_H / 2 - h / 2);
        glass(g, x, y, w, h, { r: 8 });
        g.textShadow(x + 10, y + 8, p.name, UI.goldHi, UI.ink);
        g.textR(x + w - 10, y + 8, `${p.rating}`, UI.textDim);
        MENU.forEach((label, i) => {
          const ry = y + 24 + i * 14;
          const on = i === menu!.sel;
          if (on) g.rrect(x + 6, ry - 3, w - 12, 13, 5, withA(UI.gold, 70));
          g.text(x + 12, ry, `${on ? ">" : " "}${label}`, on ? UI.goldHi : UI.text);
        });
      }
    },
  };
}

export type OnlineField = ReturnType<typeof onlineField>;

function nameTag(g: Gfx, x: number, y: number, name: string, c: Col, status: string, title?: string, o: { frame?: boolean; plaque?: string; wallet?: boolean } = {}): void {
  // Engraved Plaque: the lead Crown's number rides after the name, in brass.
  const plaque = o.plaque ? ` ${o.plaque.split(" ").pop()}` : "";
  // Wallet Verified: a small teal check before the name (the address itself is never shown).
  const mark = o.wallet ? 7 : 0;
  const w = measureText(name) + (plaque ? measureText(plaque) : 0) + mark + (status === "idle" ? 8 : 15);
  const bx = Math.round(x - w / 2);
  if (title) {
    // The worn badge title rides above the name in gold.
    const tw = measureText(title) + 12;
    const tx = Math.round(x - tw / 2);
    g.rrect(tx, y - 11, tw, 10, 4, [60, 42, 10, 190]);
    g.text(tx + 3, y - 10, "★", UI.gold);
    g.text(tx + 9, y - 10, title, UI.goldHi);
  }
  g.rrect(bx, y - 1, w, 10, 4, [10, 12, 26, 170]);
  // Crown Frame: a gold rim around the tag.
  if (o.frame) g.rrectLine(bx - 1, y - 2, w + 2, 12, 5, UI.gold);
  if (mark) g.text(bx + 4, y, "✓", UI.teal);
  g.text(bx + 4 + mark, y, name, c);
  if (plaque) g.text(bx + 4 + mark + measureText(name), y, plaque, [226, 172, 86, 255]);
  if (status !== "idle") g.disc(bx + w - 6, y + 4, 2.5, status === "duel" ? UI.pink : status === "raid" ? UI.gold : UI.teal);
}

function chatBubble(g: Gfx, x: number, y: number, text: string): void {
  const rows = wrapPx(text, 120).slice(0, 2);
  const w = Math.max(...rows.map((r) => measureText(r))) + 10;
  const h = rows.length * 10 + 6;
  const bx = Math.round(x - w / 2), by = y - h;
  g.rrect(bx, by, w, h, 4, UI.paperInk);
  g.rrect(bx + 1, by + 1, w - 2, h - 2, 3, UI.paper);
  g.fill(x - 1, by + h - 1, 3, 2, UI.paper);
  g.p(x, by + h + 1, UI.paperInk);
  rows.forEach((r, i) => g.text(bx + 5, by + 4 + i * 10, r, UI.paperInk));
}

function emoteBubble(g: Gfx, x: number, y: number, glyph: string, age: number): void {
  const pop = Math.min(1, age / 120);
  const w = measureText(glyph) + 10, h = 13;
  const bx = Math.round(x - w / 2), by = Math.round(y - h - (1 - pop) * 4);
  g.rrect(bx, by, w, h, 5, UI.ink);
  g.rrect(bx + 1, by + 1, w - 2, h - 2, 4, glyph === "♥" ? UI.pink : UI.gold);
  g.text(bx + 5, by + 3, glyph, UI.ink);
}
